import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import api from "../../services/api";
import { useToast } from "../../context/ToastContext";
import theme from "../../styles/theme";

// The search box is narrow at teleprompter width, so the dropdown breaks out of
// its container and sizes to the viewport instead of inheriting ~120px.
const SearchContainer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: max(240px, 60vw);
  max-width: calc(100vw - 1.25rem);
  background: ${theme.color.surface};
  border: 1px solid ${theme.color.borderSubtle};
  box-shadow: ${theme.shadow.panel};
  border-radius: ${theme.radius.md};
  max-height: 60vh;
  overflow-y: auto;
  z-index: ${theme.z.panel};
`;

const SearchResult = styled.div`
  padding: ${theme.space.sm} ${theme.space.md};
  border-bottom: 1px solid ${theme.color.borderSubtle};

  &:last-child {
    border-bottom: none;
  }
`;

const TopicName = styled.h3`
  font-size: ${theme.font.xs};
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.color.textFaint};
  margin-bottom: ${theme.space.xs};
  cursor: pointer;

  &:hover {
    color: ${theme.color.text};
  }
`;

const Hit = styled.div`
  padding: ${theme.space.xs};
  border-radius: ${theme.radius.sm};
  cursor: pointer;

  &:hover {
    background: ${theme.color.bg};
  }
`;

const SubtopicName = styled.h4`
  font-size: ${theme.font.md};
  font-weight: 500;
  color: ${theme.color.text};
`;

const ContentPreview = styled.p`
  font-size: ${theme.font.sm};
  color: ${theme.color.textFaint};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const NoResults = styled.div`
  padding: ${theme.space.md};
  text-align: center;
  color: ${theme.color.textFaint};
  font-size: ${theme.font.sm};
`;

const Search = ({ searchQuery, onClose }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (searchQuery.trim().length < 2) {
        setResults([]);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(
          `/topics/search?query=${encodeURIComponent(searchQuery)}`
        );
        setResults(response.data);
      } catch (error) {
        console.error("Search error:", error);
        showToast("Error performing search", "error");
      } finally {
        setLoading(false);
      }
    }, 300); // Debounce search

    return () => clearTimeout(searchTimeout);
  }, [searchQuery, showToast]);

  const handleResultClick = (topicId, subtopicId) => {
    navigate(`/topic/${topicId}`, { 
      state: { 
        openSubtopicId: subtopicId 
      } 
    });
    onClose();
  };

  if (!searchQuery.trim()) {
    return null;
  }

  return (
    <SearchContainer>
      {loading ? (
        <NoResults>Searching...</NoResults>
      ) : results.length === 0 ? (
        <NoResults>No results found</NoResults>
      ) : (
        results.map((topic) => (
          <SearchResult key={topic.id}>
            <TopicName onClick={() => handleResultClick(topic.id)}>
              {topic.name}
            </TopicName>
            {topic.matchedSubtopics.map((subtopic) => (
              <Hit
                key={subtopic.id}
                onClick={() => handleResultClick(topic.id, subtopic.id)}
              >
                <SubtopicName>{subtopic.name}</SubtopicName>
                <ContentPreview>{subtopic.preview}</ContentPreview>
              </Hit>
            ))}
          </SearchResult>
        ))
      )}
    </SearchContainer>
  );
};

export default Search;
